<template>
  <div>
    <v-container fluid>
      <v-skeleton-loader
        :loading="!flagNav"
        transition="scale-transition"
        max-height="64"
        type="table-heading"
      >
        <v-toolbar color="primary" class="header-title text-white" data-testid="tax-config-v2-toolbar">
          <v-toolbar-title class="title">Tax Configuration V2</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <v-card class="card2">
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" md="6">
              <div class="text-subtitle-1 font-weight-medium">
                <v-icon class="mr-1">mdi-calendar-multiple</v-icon>
                Tax year list
              </div>
              <p class="text-caption text-grey mt-1">
                กางปีภาษีเพื่อดูเวอร์ชันคอนฟิก แล้วกางเวอร์ชันเพื่อจัดการชุดคำถามและชุดสูตรคำนวณของเวอร์ชันนั้น
              </p>
            </v-col>

            <v-col cols="12" md="6" class="d-flex flex-wrap justify-end ga-2">
              <v-btn
                data-testid="tbc-open-btn"
                color="primary"
                class="control-btn"
                variant="outlined"
                size="large"
                prepend-icon="mdi-compare"
                rounded="lg"
                @click="bundleCompareDialog = true"
              >
                COMPARE JSON
                <v-tooltip activator="parent" location="top">
                  เทียบไฟล์ export (JSON) 2 ไฟล์ — ชุดคำถาม / ชุดสูตรคำนวณ ข้ามปี ข้ามเครื่องก็ได้
                </v-tooltip>
              </v-btn>
              <v-btn
                data-testid="mobile-ui-settings-btn"
                color="primary"
                class="control-btn"
                variant="outlined"
                size="large"
                prepend-icon="mdi-cellphone-cog"
                rounded="lg"
                @click="mobileUiSettingsDialog = true"
              >
                MOBILE UI SETTINGS
                <v-tooltip activator="parent" location="top">ตั้งค่าการแสดงผลผลิตภัณฑ์บน Mobile UI</v-tooltip>
              </v-btn>
              <v-btn
                data-testid="add-year-v2-btn"
                color="primary"
                class="control-btn create-btn"
                variant="flat"
                size="large"
                prepend-icon="mdi-plus-circle"
                rounded="lg"
                @click="openAddYear"
              >
                ADD YEAR
                <v-tooltip activator="parent" location="top">เพิ่มปีภาษีใหม่</v-tooltip>
              </v-btn>
              <v-btn
                data-testid="tpsv2-settings-btn"
                color="primary"
                class="control-btn"
                variant="outlined"
                size="large"
                prepend-icon="mdi-cog"
                rounded="lg"
                @click="openSettings"
              >
                SETTINGS
                <v-tooltip activator="parent" location="top">
                  ตั้งค่าข้อความ / prompt กลางของ Tax Planning V2
                </v-tooltip>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>

        <v-data-table
          data-testid="tax-year-v2-table"
          :headers="headers"
          :items="sortedYears"
          class="elevation-1 my-data-table"
          density="comfortable"
          :items-per-page="-1"
          fixed-header
          hide-default-footer
          hide-default-body
        >
          <template v-slot:colgroup>
            <col
              v-for="header in headers"
              :key="header.key"
              :style="{ width: header.width, minWidth: header.width }"
            />
          </template>

          <template v-slot:tbody>
            <!-- first page load -->
            <tbody v-if="yearsLoading" class="v-data-table__tbody">
              <tr>
                <td :colspan="headers.length" class="text-center py-8" data-testid="years-v2-loading">
                  <v-progress-circular indeterminate size="20" width="2" color="primary" class="mr-2" />
                  กำลังโหลดปีภาษี...
                </td>
              </tr>
            </tbody>

            <tbody v-else-if="sortedYears.length" class="v-data-table__tbody">
              <template v-for="(year, index) in sortedYears" :key="`y-${year.yearID}`">
                <!-- ── YEAR row ─────────────────────────────────────────── -->
                <tr class="cfg-year-parent-row" :data-testid="`year-row-${year.yearID}`">
                  <td class="text-center">{{ index + 1 }}</td>
                  <td>
                    <button
                      class="cfg-tree-toggle"
                      type="button"
                      :data-testid="`year-expand-${year.yearID}`"
                      :aria-expanded="isYearExpanded(year.yearID)"
                      :aria-label="`แสดง/ซ่อนเวอร์ชันของปี ${year.taxYearCode}`"
                      @click="toggleYearExpand(year)"
                    >
                      <v-icon
                        :class="['cfg-tree-chevron', { 'is-open': isYearExpanded(year.yearID) }]"
                        size="18"
                      >
                        mdi-chevron-right
                      </v-icon>
                      <span class="cfg-tree-title-block">
                        <span class="cfg-tree-title">{{ year.taxYearCode }}</span>
                        <span class="cfg-tree-subtitle">พ.ศ. {{ year.beYear }} / ค.ศ. {{ year.year }}</span>
                      </span>
                    </button>
                  </td>
                  <td>{{ year.description || '-' }}</td>
                  <td>{{ formatDate(year.effectiveStartDate) }}</td>
                  <td>{{ formatDate(year.effectiveEndDate) }}</td>
                  <td>{{ formatUpdated(year.updatedDate) }}</td>
                  <td class="text-center">
                    <v-switch
                      :model-value="year.isActive"
                      :data-testid="`year-active-${year.yearID}`"
                      :aria-label="`เปิดใช้งานปี ${year.taxYearCode}`"
                      color="success"
                      density="compact"
                      hide-details
                      inset
                      @update:model-value="toggleYearActive(year, $event)"
                    />
                  </td>
                  <td class="text-center">
                    <div class="action-group">
                      <v-btn
                        :data-testid="`add-version-${year.yearID}`"
                        variant="tonal"
                        density="comfortable"
                        icon
                        color="primary"
                        class="action-btn"
                        @click="openAddVersion(year)"
                      >
                        <v-icon size="18">mdi-source-branch-plus</v-icon>
                        <v-tooltip activator="parent" location="top">เพิ่มเวอร์ชันคอนฟิก</v-tooltip>
                      </v-btn>
                      <v-btn
                        :data-testid="`import-version-${year.yearID}`"
                        variant="tonal"
                        density="comfortable"
                        icon
                        color="primary"
                        class="action-btn"
                        @click="openConfigImport(year)"
                      >
                        <v-icon size="18">mdi-import</v-icon>
                        <v-tooltip activator="parent" location="top">นำเข้าเวอร์ชันคอนฟิกจากไฟล์</v-tooltip>
                      </v-btn>
                      <v-btn
                        :data-testid="`edit-year-${year.yearID}`"
                        variant="tonal"
                        density="comfortable"
                        icon
                        color="warning"
                        class="action-btn"
                        @click="openEditYear(year)"
                      >
                        <v-icon size="18">mdi-pencil-outline</v-icon>
                        <v-tooltip activator="parent" location="top">แก้ไขปีภาษี</v-tooltip>
                      </v-btn>
                      <v-btn
                        :data-testid="`delete-year-${year.yearID}`"
                        variant="tonal"
                        density="comfortable"
                        icon
                        color="error"
                        class="action-btn"
                        @click="openDeleteYear(year)"
                      >
                        <v-icon size="18">mdi-delete-outline</v-icon>
                        <v-tooltip activator="parent" location="top">ลบปีภาษี</v-tooltip>
                      </v-btn>
                    </div>
                  </td>
                </tr>

                <!-- ── CONFIG VERSION children (lazy per year) ──────────── -->
                <template v-if="isYearExpanded(year.yearID)">
                  <tr
                    v-if="isVersionsLoading(year.yearID)"
                    :key="`y-${year.yearID}-vloading`"
                    class="cfg-version-child-row"
                  >
                    <td />
                    <td colspan="7" class="cfg-subtree-status" :data-testid="`year-versions-loading-${year.yearID}`">
                      <v-progress-circular indeterminate size="16" width="2" color="primary" class="mr-2" />
                      กำลังโหลดเวอร์ชันคอนฟิก...
                    </td>
                  </tr>

                  <!-- a failed re-fetch keeps the last known list visible below -->
                  <tr
                    v-else-if="versionsError(year.yearID)"
                    :key="`y-${year.yearID}-verror`"
                    class="cfg-version-child-row"
                  >
                    <td />
                    <td
                      colspan="7"
                      class="cfg-subtree-status is-error"
                      :data-testid="`year-versions-error-${year.yearID}`"
                    >
                      <v-icon size="16" class="mr-1">mdi-alert-circle-outline</v-icon>
                      {{ versionsError(year.yearID) }}
                      <v-btn
                        :data-testid="`year-versions-retry-${year.yearID}`"
                        size="x-small"
                        variant="text"
                        color="error"
                        class="ml-2"
                        @click="loadVersionsForYear(year.yearID, true)"
                      >
                        ลองใหม่
                      </v-btn>
                    </td>
                  </tr>

                  <template v-if="!isVersionsLoading(year.yearID)">
                    <template
                      v-for="(version, vIdx) in versionsOf(year.yearID)"
                      :key="`v-${version.versionID}`"
                    >
                      <tr class="cfg-version-child-row" :data-testid="`version-row-${version.versionID}`">
                        <td />
                        <td class="cfg-version-child-cell">
                          <span class="cfg-version-branch" />
                          <button
                            class="cfg-tree-toggle"
                            type="button"
                            :data-testid="`cfgv2-expand-${version.versionID}`"
                            :aria-expanded="isVersionExpanded(version.versionID)"
                            :aria-label="`แสดง/ซ่อนชุดคำถามของเวอร์ชัน ${version.versionCode}`"
                            @click="toggleVersionExpand(version)"
                          >
                            <v-icon
                              :class="['cfg-tree-chevron', { 'is-open': isVersionExpanded(version.versionID) }]"
                              size="16"
                            >
                              mdi-chevron-right
                            </v-icon>
                            <span class="cfg-tree-title-block">
                              <span class="cfg-tree-title">{{ version.versionCode }}</span>
                              <span class="cfg-tree-subtitle">
                                Version {{ vIdx + 1 }} of {{ versionsOf(year.yearID).length }}
                              </span>
                            </span>
                          </button>
                        </td>
                        <td>{{ version.description || '-' }}</td>
                        <td>{{ formatDate(version.effectiveStartDate) }}</td>
                        <td>{{ formatDate(version.effectiveEndDate) }}</td>
                        <td>{{ formatUpdated(version.updatedDate) }}</td>
                        <td class="text-center">
                          <v-switch
                            :model-value="version.isActive"
                            :data-testid="`version-active-${version.versionID}`"
                            :aria-label="`ตั้งเวอร์ชัน ${version.versionCode} เป็น active`"
                            :loading="busyVersionId === version.versionID"
                            :disabled="busyVersionId === version.versionID"
                            color="success"
                            density="compact"
                            hide-details
                            inset
                            @update:model-value="toggleVersionActive(year, version, $event)"
                          />
                        </td>
                        <td class="text-center">
                          <div class="action-group">
                            <v-btn
                              :data-testid="`edit-version-open-${version.versionID}`"
                              variant="tonal"
                              density="comfortable"
                              icon
                              color="warning"
                              class="action-btn"
                              @click="openEditor(year, version)"
                            >
                              <v-icon size="18">mdi-pencil-outline</v-icon>
                              <v-tooltip activator="parent" location="top">แก้ไขคอนฟิก (หมวด / กติกา)</v-tooltip>
                            </v-btn>
                            <v-btn
                              :data-testid="`version-meta-${version.versionID}`"
                              variant="tonal"
                              density="comfortable"
                              icon
                              color="primary"
                              class="action-btn"
                              @click="openEditVersion(year, version)"
                            >
                              <v-icon size="18">mdi-tune</v-icon>
                              <v-tooltip activator="parent" location="top">แก้ข้อมูลเวอร์ชัน</v-tooltip>
                            </v-btn>
                            <v-btn
                              :data-testid="`clone-version-${version.versionID}`"
                              variant="tonal"
                              density="comfortable"
                              icon
                              color="info"
                              class="action-btn"
                              @click="openClone(year, version)"
                            >
                              <v-icon size="18">mdi-content-copy</v-icon>
                              <v-tooltip activator="parent" location="top">โคลนเวอร์ชัน</v-tooltip>
                            </v-btn>
                            <v-btn
                              :data-testid="`export-version-${version.versionID}`"
                              variant="tonal"
                              density="comfortable"
                              icon
                              color="info"
                              class="action-btn"
                              :loading="busyVersionId === version.versionID"
                              :disabled="busyVersionId === version.versionID"
                              @click="openConfigExport(year, version)"
                            >
                              <v-icon size="18">mdi-export</v-icon>
                              <v-tooltip activator="parent" location="top">ส่งออกเป็นไฟล์</v-tooltip>
                            </v-btn>
                            <v-btn
                              :data-testid="`delete-version-${version.versionID}`"
                              variant="tonal"
                              density="comfortable"
                              icon
                              color="error"
                              class="action-btn"
                              @click="openDeleteVersion(year, version)"
                            >
                              <v-icon size="18">mdi-delete-outline</v-icon>
                              <v-tooltip activator="parent" location="top">ลบเวอร์ชัน</v-tooltip>
                            </v-btn>
                          </div>
                        </td>
                      </tr>

                      <!-- ── Tax Question sub-group (bound to THIS config version) ── -->
                      <template v-if="isVersionExpanded(version.versionID)">
                        <tr
                          v-if="isQuestionLoading(version.versionID)"
                          :key="`v-${version.versionID}-qloading`"
                          class="cfg-subtree-row"
                        >
                          <td />
                          <td colspan="7" class="cfg-subtree-status" :data-testid="`cfgv2-q-loading-${version.versionID}`">
                            <v-progress-circular indeterminate size="16" width="2" color="primary" class="mr-2" />
                            กำลังโหลดชุดคำถาม...
                          </td>
                        </tr>

                        <!-- a failed re-fetch keeps the last known list visible below -->
                        <tr
                          v-else-if="questionError(version.versionID)"
                          :key="`v-${version.versionID}-qerror`"
                          class="cfg-subtree-row"
                        >
                          <td />
                          <td
                            colspan="7"
                            class="cfg-subtree-status is-error"
                            :data-testid="`cfgv2-q-error-${version.versionID}`"
                          >
                            <v-icon size="16" class="mr-1">mdi-alert-circle-outline</v-icon>
                            {{ questionError(version.versionID) }}
                            <v-btn
                              :data-testid="`cfgv2-q-retry-${version.versionID}`"
                              size="x-small"
                              variant="text"
                              color="error"
                              class="ml-2"
                              @click="loadQuestionVersions(version.versionID, true)"
                            >
                              ลองใหม่
                            </v-btn>
                          </td>
                        </tr>

                        <template
                          v-if="!isQuestionLoading(version.versionID)
                            && (!questionError(version.versionID) || questionVersionsFor(version.versionID).length)"
                        >
                          <tr
                            :key="`v-${version.versionID}-qhead`"
                            :data-testid="`cfgv2-q-group-${version.versionID}`"
                            class="cfg-subgroup-row cfg-subgroup-question"
                          >
                            <td />
                            <td colspan="7" class="cfg-subgroup-cell">
                              <span class="cfg-subgroup-branch" />
                              <span class="cfg-subgroup-header">
                                <v-icon size="16" color="primary" class="mr-1">mdi-comment-question-outline</v-icon>
                                <span class="cfg-subgroup-title">Tax Question</span>
                                <span class="cfg-subgroup-count">
                                  ({{ questionVersionsFor(version.versionID).length }})
                                </span>
                              </span>
                              <span class="cfg-subgroup-actions">
                                <v-btn
                                  :data-testid="`cfgv2-q-add-${version.versionID}`"
                                  size="small"
                                  variant="tonal"
                                  color="primary"
                                  prepend-icon="mdi-plus"
                                  @click="addQuestionVersion(version.versionID)"
                                >
                                  เพิ่ม
                                </v-btn>
                                <v-btn
                                  :data-testid="`cfgv2-q-import-${version.versionID}`"
                                  size="small"
                                  variant="tonal"
                                  prepend-icon="mdi-import"
                                  @click="openQuestionImport(version.versionID)"
                                >
                                  นำเข้า
                                </v-btn>
                              </span>
                            </td>
                          </tr>

                          <tr
                            v-for="qv in questionVersionsFor(version.versionID)"
                            :key="`q-${qv.questionVersionID}`"
                            :data-testid="`cfgv2-q-row-${qv.questionVersionID}`"
                            class="cfg-subversion-row cfg-subversion-question"
                          >
                            <td />
                            <td class="cfg-subversion-cell">
                              <span class="cfg-subversion-branch" />
                              <span class="cfg-subversion-code">{{ qv.versionCode }}</span>
                            </td>
                            <td>{{ qv.description || '-' }}</td>
                            <td />
                            <td />
                            <td>{{ formatUpdated(qv.updatedDate) }}</td>
                            <td class="text-center">
                              <v-switch
                                :model-value="qv.isActive"
                                :data-testid="`cfgv2-q-active-${qv.questionVersionID}`"
                                :aria-label="`ตั้งชุดคำถาม ${qv.versionCode} เป็น active`"
                                :loading="busyQuestionId === qv.questionVersionID"
                                :disabled="busyQuestionId === qv.questionVersionID"
                                color="success"
                                density="compact"
                                hide-details
                                inset
                                @update:model-value="toggleQuestionActive(version.versionID, qv, $event)"
                              />
                            </td>
                            <td class="text-center">
                              <div class="action-group">
                                <v-btn
                                  :data-testid="`cfgv2-q-edit-${qv.questionVersionID}`"
                                  variant="tonal"
                                  density="comfortable"
                                  size="small"
                                  color="warning"
                                  prepend-icon="mdi-pencil-outline"
                                  @click="openQuestionEditor(version.versionID, qv.questionVersionID)"
                                >
                                  Edit
                                </v-btn>
                                <v-menu location="bottom end">
                                  <template #activator="{ props }">
                                    <v-btn
                                      v-bind="props"
                                      :data-testid="`cfgv2-q-menu-${qv.questionVersionID}`"
                                      variant="tonal"
                                      density="comfortable"
                                      icon
                                      size="small"
                                      color="grey-darken-1"
                                      class="action-btn"
                                      :aria-label="`เมนูชุดคำถาม ${qv.versionCode}`"
                                    >
                                      <v-icon size="18">mdi-dots-vertical</v-icon>
                                    </v-btn>
                                  </template>
                                  <v-list density="compact">
                                    <v-list-item
                                      :data-testid="`cfgv2-q-meta-${qv.questionVersionID}`"
                                      prepend-icon="mdi-pencil-outline"
                                      title="แก้ข้อมูลชุด"
                                      @click="openQuestionMeta(version.versionID, qv)"
                                    />
                                    <v-list-item
                                      :data-testid="`cfgv2-q-clone-${qv.questionVersionID}`"
                                      prepend-icon="mdi-content-copy"
                                      title="โคลนทั้งชุด"
                                      @click="openQuestionClone(version.versionID, qv)"
                                    />
                                    <v-list-item
                                      :data-testid="`cfgv2-q-export-${qv.questionVersionID}`"
                                      prepend-icon="mdi-export"
                                      title="ส่งออกเป็นไฟล์"
                                      @click="onExportQuestionVersion(qv)"
                                    />
                                    <v-list-item
                                      :data-testid="`cfgv2-q-del-${qv.questionVersionID}`"
                                      prepend-icon="mdi-delete-outline"
                                      title="ลบชุด"
                                      base-color="error"
                                      @click="openDeleteQuestionVersion(version.versionID, qv)"
                                    />
                                  </v-list>
                                </v-menu>
                              </div>
                            </td>
                          </tr>

                          <tr
                            v-if="!questionVersionsFor(version.versionID).length"
                            :key="`v-${version.versionID}-qempty`"
                            class="cfg-subversion-row cfg-subversion-empty"
                          >
                            <td />
                            <td colspan="7" class="cfg-subversion-empty-cell" :data-testid="`cfgv2-q-empty-${version.versionID}`">
                              — ยังไม่มีชุดคำถาม —
                            </td>
                          </tr>
                        </template>

                        <!-- ── Tax Calculate sub-group (same config version, same shape as Tax Question) ── -->
                        <tr
                          v-if="isCalcLoading(version.versionID)"
                          :key="`v-${version.versionID}-cloading`"
                          class="cfg-subtree-row"
                        >
                          <td />
                          <td colspan="7" class="cfg-subtree-status" :data-testid="`cfgv2-c-loading-${version.versionID}`">
                            <v-progress-circular indeterminate size="16" width="2" color="deep-purple" class="mr-2" />
                            กำลังโหลดชุดสูตรคำนวณ...
                          </td>
                        </tr>

                        <!-- a failed re-fetch keeps the last known list visible below -->
                        <tr
                          v-else-if="calcError(version.versionID)"
                          :key="`v-${version.versionID}-cerror`"
                          class="cfg-subtree-row"
                        >
                          <td />
                          <td
                            colspan="7"
                            class="cfg-subtree-status is-error"
                            :data-testid="`cfgv2-c-error-${version.versionID}`"
                          >
                            <v-icon size="16" class="mr-1">mdi-alert-circle-outline</v-icon>
                            {{ calcError(version.versionID) }}
                            <v-btn
                              :data-testid="`cfgv2-c-retry-${version.versionID}`"
                              size="x-small"
                              variant="text"
                              color="error"
                              class="ml-2"
                              @click="loadCalcVersions(version.versionID, true)"
                            >
                              ลองใหม่
                            </v-btn>
                          </td>
                        </tr>

                        <template
                          v-if="!isCalcLoading(version.versionID)
                            && (!calcError(version.versionID) || calcVersionsFor(version.versionID).length)"
                        >
                          <tr
                            :key="`v-${version.versionID}-chead`"
                            :data-testid="`cfgv2-c-group-${version.versionID}`"
                            class="cfg-subgroup-row cfg-subgroup-calc"
                          >
                            <td />
                            <td colspan="7" class="cfg-subgroup-cell">
                              <span class="cfg-subgroup-branch" />
                              <span class="cfg-subgroup-header">
                                <v-icon size="16" color="deep-purple" class="mr-1">mdi-calculator-variant-outline</v-icon>
                                <span class="cfg-subgroup-title">Tax Calculate</span>
                                <span class="cfg-subgroup-count">
                                  ({{ calcVersionsFor(version.versionID).length }})
                                </span>
                              </span>
                              <span class="cfg-subgroup-actions">
                                <v-btn
                                  :data-testid="`cfgv2-c-add-${version.versionID}`"
                                  size="small"
                                  variant="tonal"
                                  color="deep-purple"
                                  prepend-icon="mdi-plus"
                                  @click="addCalcVersion(version.versionID)"
                                >
                                  เพิ่ม
                                </v-btn>
                                <v-btn
                                  :data-testid="`cfgv2-c-import-${version.versionID}`"
                                  size="small"
                                  variant="tonal"
                                  prepend-icon="mdi-import"
                                  @click="openCalcImport(version.versionID)"
                                >
                                  นำเข้า
                                </v-btn>
                              </span>
                            </td>
                          </tr>

                          <tr
                            v-for="cv in calcVersionsFor(version.versionID)"
                            :key="`c-${cv.calcVersionID}`"
                            :data-testid="`cfgv2-c-row-${cv.calcVersionID}`"
                            class="cfg-subversion-row cfg-subversion-calc"
                          >
                            <td />
                            <td class="cfg-subversion-cell">
                              <span class="cfg-subversion-branch" />
                              <span class="cfg-subversion-code">{{ cv.versionCode }}</span>
                            </td>
                            <td>{{ cv.description || '-' }} ({{ cv.functionCount || 0 }} ฟังก์ชัน)</td>
                            <!-- calc versions carry no dates of their own — they follow the parent config version -->
                            <td>{{ formatDate(version.effectiveStartDate) }}</td>
                            <td>{{ formatDate(version.effectiveEndDate) }}</td>
                            <td>{{ formatUpdated(cv.updatedDate) }}</td>
                            <td class="text-center">
                              <v-switch
                                :model-value="cv.isActive"
                                :data-testid="`cfgv2-c-active-${cv.calcVersionID}`"
                                :aria-label="`ตั้งชุดสูตร ${cv.versionCode} เป็น active`"
                                :loading="busyCalcId === cv.calcVersionID"
                                :disabled="busyCalcId === cv.calcVersionID"
                                color="success"
                                density="compact"
                                hide-details
                                inset
                                @update:model-value="toggleCalcActive(version.versionID, cv, $event)"
                              />
                            </td>
                            <td class="text-center">
                              <div class="action-group">
                                <v-btn
                                  :data-testid="`cfgv2-c-edit-${cv.calcVersionID}`"
                                  variant="tonal"
                                  density="comfortable"
                                  size="small"
                                  color="warning"
                                  prepend-icon="mdi-pencil-outline"
                                  @click="openCalcEditor(version.versionID, cv.calcVersionID)"
                                >
                                  Edit
                                </v-btn>
                                <v-menu location="bottom end">
                                  <template #activator="{ props }">
                                    <v-btn
                                      v-bind="props"
                                      :data-testid="`cfgv2-c-menu-${cv.calcVersionID}`"
                                      variant="tonal"
                                      density="comfortable"
                                      icon
                                      size="small"
                                      color="grey-darken-1"
                                      class="action-btn"
                                      :aria-label="`เมนูชุดสูตร ${cv.versionCode}`"
                                    >
                                      <v-icon size="18">mdi-dots-vertical</v-icon>
                                    </v-btn>
                                  </template>
                                  <v-list density="compact">
                                    <v-list-item
                                      :data-testid="`cfgv2-c-meta-${cv.calcVersionID}`"
                                      prepend-icon="mdi-pencil-outline"
                                      title="แก้ข้อมูลชุด"
                                      @click="openCalcMeta(version.versionID, cv)"
                                    />
                                    <v-list-item
                                      :data-testid="`cfgv2-c-clone-${cv.calcVersionID}`"
                                      prepend-icon="mdi-content-copy"
                                      title="โคลนทั้งชุด"
                                      @click="openCalcClone(version.versionID, cv)"
                                    />
                                    <v-list-item
                                      :data-testid="`cfgv2-c-export-${cv.calcVersionID}`"
                                      prepend-icon="mdi-export"
                                      title="ส่งออกเป็นไฟล์"
                                      @click="onExportCalcVersion(cv)"
                                    />
                                    <v-list-item
                                      :data-testid="`cfgv2-c-del-${cv.calcVersionID}`"
                                      prepend-icon="mdi-delete-outline"
                                      title="ลบชุด"
                                      base-color="error"
                                      @click="openDeleteCalcVersion(version.versionID, cv)"
                                    />
                                  </v-list>
                                </v-menu>
                              </div>
                            </td>
                          </tr>

                          <tr
                            v-if="!calcVersionsFor(version.versionID).length"
                            :key="`v-${version.versionID}-cempty`"
                            class="cfg-subversion-row cfg-subversion-empty"
                          >
                            <td />
                            <td colspan="7" class="cfg-subversion-empty-cell" :data-testid="`cfgv2-c-empty-${version.versionID}`">
                              — ยังไม่มีชุดสูตรคำนวณ —
                            </td>
                          </tr>
                        </template>
                      </template>
                    </template>

                    <tr
                      v-if="!isVersionsLoading(year.yearID) && !versionsOf(year.yearID).length && !versionsError(year.yearID)"
                      :key="`y-${year.yearID}-vempty`"
                      class="cfg-version-child-row"
                    >
                      <td />
                      <td colspan="7" class="text-grey text-caption" :data-testid="`year-versions-empty-${year.yearID}`">
                        ยังไม่มีเวอร์ชันคอนฟิก — กด "เพิ่มเวอร์ชันคอนฟิก" เพื่อสร้างเวอร์ชันแรก
                      </td>
                    </tr>
                  </template>
                </template>
              </template>
            </tbody>

            <tbody v-else class="v-data-table__tbody">
              <tr>
                <td :colspan="headers.length">
                  <div class="no-data-container" data-testid="years-v2-empty">
                    <div class="no-data-content">
                      <v-icon size="64" color="grey-lighten-2" class="no-data-icon">
                        mdi-calendar-blank-outline
                      </v-icon>
                      <div class="no-data-text">
                        <h3 class="no-data-title">ยังไม่มีปีภาษี</h3>
                        <p class="no-data-subtitle">สร้างปีภาษีแรกเพื่อเริ่มตั้งค่า Tax Configuration V2</p>
                      </div>
                      <v-btn color="primary" variant="tonal" prepend-icon="mdi-plus" @click="openAddYear">
                        เพิ่มปีแรก
                      </v-btn>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </v-card>
    </v-container>

    <!-- Year dialog (add / edit) -->
    <TaxYearV2Dialog
      v-model="yearDialog"
      :year-options="yearOptions"
      :initial-data="yearEditTarget"
      @submit="submitYear"
    />

    <!-- Config version dialog (add / edit metadata / clone) -->
    <TaxConfigV2Dialog
      v-model="versionDialog"
      :mode="versionDialogMode"
      :initial-data="versionEditTarget"
      :source-version="cloneSource"
      @submit="submitVersionDialog"
    />

    <!-- Export ONE config version (header + ALL categories + ALL rules + the ticked calc /
         question sets) as a .json bundle. The dialog only collects the selection — this page
         calls the API, makes the download and closes it. -->
    <TaxConfigExportV2Dialog
      v-model="cfgExportDialog"
      :config-version-id="cfgExportVersion ? cfgExportVersion.versionID : null"
      :config-version-label="cfgExportLabel"
      :calc-versions="cfgExportCalcVersions"
      :question-versions="cfgExportQuestionVersions"
      :busy="cfgExportBusy"
      @export="onConfigExport"
    />

    <!-- Import a config-version bundle — always lands as a NEW inactive config version under
         the tax year whose «นำเข้า» button was pressed (never the file's `source.taxYearCode`).
         `year-label` is the target's taxYearCode: the dialog compares it with the file's to warn
         about a cross-year import. -->
    <TaxConfigImportV2Dialog
      v-model="cfgImportDialog"
      :year-id="cfgImportYear ? cfgImportYear.yearID : null"
      :year-label="cfgImportYear ? cfgImportYear.taxYearCode : ''"
      :suggested-code="cfgImportSuggestedCode"
      @imported="onConfigImported"
    />

    <!-- Question version dialog (edit metadata / clone whole set) -->
    <TaxQuestionVersionV2Dialog
      v-model="qVersionDialog"
      :mode="qVersionDialogMode"
      :initial-data="qVersionTarget"
      :suggested-code="qVersionSuggestedCode"
      :saving="qVersionSaving"
      @submit="submitQuestionVersionDialog"
    />

    <!-- Import a question set from a .json bundle — always lands as a NEW draft set
         under the config version whose «นำเข้า» button was pressed. -->
    <TaxQuestionImportV2Dialog
      v-model="qImportDialog"
      :config-version-id="qImportParentId"
      :config-version-label="qImportLabel"
      :suggested-code="qImportSuggestedCode"
      @imported="onQuestionImported"
    />

    <TaxCalcVersionV2Dialog
      v-model="cVersionDialog"
      :mode="cVersionDialogMode"
      :initial-data="cVersionTarget"
      :suggested-code="cVersionSuggestedCode"
      :saving="cVersionSaving"
      @submit="submitCalcVersionDialog"
    />

    <!-- Import a formula set from a .json bundle — always lands as a NEW draft set
         under the config version whose «นำเข้า» button was pressed. -->
    <TaxCalcImportV2Dialog
      v-model="cImportDialog"
      :config-version-id="cImportParentId"
      :config-version-label="cImportLabel"
      :suggested-code="cImportSuggestedCode"
      @imported="onCalcImported"
    />

    <!-- Global Tax Planning V2 prompt settings (singleton — not per year/version) -->
    <TaxPlanningSettingsV2Dialog
      v-model="settingsDialog"
      @saved="showSuccess"
      @error="showError"
    />

    <!-- Standalone JSON-file compare — no year / version / API involved, just two export files. -->
    <TaxBundleCompareV2Dialog v-model="bundleCompareDialog" />

    <!-- Persistent mobile recommendation-product display settings. -->
    <TaxMobileProductSettingsDialog v-model="mobileUiSettingsDialog" />

    <!-- Delete confirm (year | config version | question version) -->
    <v-dialog v-model="deleteDialog" max-width="520px" persistent data-testid="tax-config-v2-delete-dialog">
      <v-card>
        <v-card-title class="bg-error text-white">
          <v-icon class="mr-2">mdi-alert-circle</v-icon>
          ยืนยันการลบ
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" variant="tonal" class="mb-4">
            {{ deleteCascadeMessage }}
          </v-alert>
          <v-alert
            v-if="deleteWarnZeroActive"
            type="error"
            variant="tonal"
            density="comfortable"
            class="mb-4"
            data-testid="delete-zero-active-warning"
          >
            {{ deleteZeroActiveMessage }}
          </v-alert>
          <p>{{ deleteMessage }}</p>
          <p class="font-weight-bold text-primary">{{ deleteTargetLabel }}</p>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn data-testid="delete-cancel-btn" variant="outlined" @click="deleteDialog = false">
            <v-icon start>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn
            data-testid="delete-confirm-btn"
            color="error"
            variant="elevated"
            :loading="deleting"
            @click="confirmDelete"
          >
            <v-icon start>mdi-delete</v-icon>
            ลบ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" location="top" timeout="3500">
      {{ snackbarMessage }}
      <template #actions>
        <v-btn variant="text" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { taxConfigV2Store } from '../utils/taxConfigV2Store';
import { taxConfigV2Api } from '../services/taxConfigV2Api';
import { taxQuestionV2Api } from '../services/taxQuestionV2Api';
import { taxCalculateV2Api } from '../services/taxCalculateV2Api';
import { fmtThaiDate } from '../utils/thaiTime.js';
import TaxYearV2Dialog from '../components/TaxYearV2Dialog.vue';
import TaxConfigV2Dialog from '../components/TaxConfigV2Dialog.vue';
import TaxConfigExportV2Dialog from '../components/TaxConfigExportV2Dialog.vue';
import TaxConfigImportV2Dialog from '../components/TaxConfigImportV2Dialog.vue';
import TaxQuestionVersionV2Dialog from '../components/TaxQuestionVersionV2Dialog.vue';
import TaxQuestionImportV2Dialog from '../components/TaxQuestionImportV2Dialog.vue';
import TaxCalcVersionV2Dialog from '../components/TaxCalcVersionV2Dialog.vue';
import TaxCalcImportV2Dialog from '../components/TaxCalcImportV2Dialog.vue';
import TaxPlanningSettingsV2Dialog from '../components/TaxPlanningSettingsV2Dialog.vue';
import TaxMobileProductSettingsDialog from '../components/TaxMobileProductSettingsDialog.vue';
import TaxBundleCompareV2Dialog from '../components/TaxBundleCompareV2Dialog.vue';

export default {
  name: 'TaxConfigurationV2',
  components: {
    TaxYearV2Dialog,
    TaxConfigV2Dialog,
    TaxConfigExportV2Dialog,
    TaxConfigImportV2Dialog,
    TaxQuestionVersionV2Dialog,
    TaxQuestionImportV2Dialog,
    TaxCalcVersionV2Dialog,
    TaxCalcImportV2Dialog,
    TaxPlanningSettingsV2Dialog,
    TaxMobileProductSettingsDialog,
    TaxBundleCompareV2Dialog,
  },
  data() {
    const currentBE = new Date().getFullYear() + 543;
    return {
      flagNav: true,
      today: new Date().toLocaleDateString(),
      store: taxConfigV2Store.state,
      headers: [
        { title: 'No.', key: 'no', align: 'center', sortable: false, width: '70px' },
        { title: 'Year / Version', key: 'taxYear', align: 'start', sortable: false, width: '240px' },
        { title: 'Description', key: 'description', align: 'start', sortable: false, width: '260px' },
        { title: 'Effective Start Date', key: 'startDate', align: 'start', sortable: false, width: '170px' },
        { title: 'Effective End Date', key: 'endDate', align: 'start', sortable: false, width: '170px' },
        { title: 'Updated Date', key: 'updatedDate', align: 'start', sortable: false, width: '150px' },
        { title: 'Active', key: 'isActive', align: 'center', sortable: false, width: '110px' },
        { title: 'Action', key: 'actions', align: 'center', sortable: false, width: '280px' },
      ],
      yearsLoading: false,
      busyVersionId: null,
      yearOptions: Array.from({ length: 7 }, (_, i) => currentBE - 1 + i),
      // tree expansion (year rows → config version rows)
      expandedYears: {},           // { [yearID]: true }
      expandedVersions: {},        // { [versionID]: true }
      // year dialog
      yearDialog: false,
      yearEditTarget: null,
      // config version / clone dialog
      versionDialog: false,
      versionDialogMode: 'version',
      versionEditTarget: null,
      cloneSource: null,
      versionYearId: null,         // year the version dialog is working in
      // config-version bundle export (header + categories + rules + the ticked calc/question sets)
      // standalone «เทียบไฟล์ JSON» — ไม่ผูกกับปี/เวอร์ชันไหนเลย ทุกอย่างมาจากไฟล์ที่ผู้ใช้ใส่เข้ามา
      bundleCompareDialog: false,
      cfgExportDialog: false,
      cfgExportYear: null,         // year DTO of the row whose «ส่งออก» was pressed (file name + label)
      cfgExportVersion: null,      // config version DTO being exported
      // config-version bundle import (bundle .json → a NEW inactive config version under ONE year)
      cfgImportDialog: false,
      cfgImportYear: null,         // year DTO the import lands under — the target, never the file
      // Tax Question sub-tree (per config version — a question set binds to the
      // FieldKeys of ITS config version, so it is authored from inside this page).
      questionVersionsByVersion: {}, // { [versionID]: TaxQuestionVersionV2Dto[] }
      qLoading: {},                // { [versionID]: boolean }
      qError: {},                  // { [versionID]: string }
      qSeq: {},                    // { [versionID]: number } — request sequence, out-of-order guard
      busyQuestionId: null,        // question version whose active toggle is in flight
      // question version dialog (edit metadata / clone whole set)
      qVersionDialog: false,
      qVersionDialogMode: 'edit',  // 'edit' | 'clone'
      qVersionTarget: null,
      qVersionParentId: null,      // config versionID owning the question version
      qVersionSuggestedCode: '',
      qVersionSaving: false,
      // question-set import dialog (bundle .json → a NEW draft set under ONE config version)
      qImportDialog: false,
      qImportParentId: null,       // config versionID the import lands under — the target, never the file
      // Tax Calculate sub-tree (same shape as the question one — a formula set binds to the
      // FieldKeys of ITS config version, so it is authored from inside this page too).
      calcVersionsByVersion: {}, // { [versionID]: TaxCalcVersionV2Dto[] }
      cLoading: {},                // { [versionID]: boolean }
      cError: {},                  // { [versionID]: string }
      cSeq: {},                    // { [versionID]: number } — request sequence, out-of-order guard
      busyCalcId: null,            // calc version whose active toggle is in flight
      // calc version dialog (create / edit metadata / clone whole set)
      cVersionDialog: false,
      cVersionDialogMode: 'create', // 'create' | 'edit' | 'clone'
      cVersionTarget: null,
      cVersionParentId: null,      // config versionID owning the calc version
      cVersionSuggestedCode: '',
      cVersionSaving: false,
      // formula-set import dialog (bundle .json → a NEW draft set under ONE config version)
      cImportDialog: false,
      cImportParentId: null,       // config versionID the import lands under — the target, never the file
      // global Tax Planning V2 prompt settings dialog (singleton, loads on open)
      settingsDialog: false,
      // mobile product display settings dialog state
      mobileUiSettingsDialog: false,
      // delete
      deleteDialog: false,
      deleteKind: '', // 'year' | 'version' | 'questionVersion' | 'calcVersion'
      deleteTarget: null,
      deleteYearId: null,          // year owning the config version being deleted
      deleteQuestionParentId: null, // config versionID owning the question version being deleted
      deleteCalcParentId: null,    // config versionID owning the calc version being deleted
      deleting: false,
      // snackbar
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
    };
  },
  computed: {
    years() {
      return this.store.years;
    },
    sortedYears() {
      return this.years.slice().sort((a, b) => (b.beYear || 0) - (a.beYear || 0));
    },
    /** Subtitle of the import dialog — «TY2568 / V1», so the target is never ambiguous. */
    qImportLabel() {
      const version = taxConfigV2Store.findVersion(this.qImportParentId);
      if (!version) return '';
      const year = taxConfigV2Store.findYear(taxConfigV2Store.yearIdOfVersion(this.qImportParentId));
      return `${year ? year.taxYearCode + ' / ' : ''}${version.versionCode}`;
    },
    /** Fallback code for a bundle whose `source.questionVersionCode` is empty — same
     *  next-free-`Q{n}` rule the clone dialog uses, so an import never lands on a taken code. */
    qImportSuggestedCode() {
      return this.qImportParentId == null ? '' : this.suggestQuestionVersionCode(this.qImportParentId);
    },
    /** Subtitle of the calc import dialog — same «TY2568 / V1» rule as the question one. */
    cImportLabel() {
      const version = taxConfigV2Store.findVersion(this.cImportParentId);
      if (!version) return '';
      const year = taxConfigV2Store.findYear(taxConfigV2Store.yearIdOfVersion(this.cImportParentId));
      return `${year ? year.taxYearCode + ' / ' : ''}${version.versionCode}`;
    },
    /** Fallback code for a bundle whose `source.calcVersionCode` is empty — same
     *  next-free-`C{n}` rule the create/clone dialog uses, so an import never lands on a taken code. */
    cImportSuggestedCode() {
      return this.cImportParentId == null ? '' : this.suggestCalcVersionCode(this.cImportParentId);
    },
    /** Subtitle of the config export dialog — «TY2568 / V1», same rule as the import labels. */
    cfgExportLabel() {
      const version = this.cfgExportVersion;
      if (!version) return '';
      const year = this.cfgExportYear;
      return `${year ? year.taxYearCode + ' / ' : ''}${version.versionCode}`;
    },
    /** Candidate lists for the export dialog — read from the SAME per-version caches the tree
     *  renders from (`openConfigExport` warms them before the dialog opens). */
    cfgExportCalcVersions() {
      return this.cfgExportVersion ? this.calcVersionsFor(this.cfgExportVersion.versionID) : [];
    },
    cfgExportQuestionVersions() {
      return this.cfgExportVersion ? this.questionVersionsFor(this.cfgExportVersion.versionID) : [];
    },
    /** The export shares the row's `busyVersionId` with the active toggle — one row, one in-flight call. */
    cfgExportBusy() {
      return this.cfgExportVersion != null && this.busyVersionId === this.cfgExportVersion.versionID;
    },
    /** Fallback code for a bundle whose `configVersion.versionCode` is empty — next free `V{n}`
     *  in the target year, so an import never lands on a taken code. */
    cfgImportSuggestedCode() {
      return this.cfgImportYear ? this.suggestConfigVersionCode(this.cfgImportYear.yearID) : '';
    },
    deleteMessage() {
      if (this.deleteKind === 'year') return 'ต้องการลบปีภาษีนี้ใช่หรือไม่:';
      if (this.deleteKind === 'questionVersion') return 'ต้องการลบชุดคำถามนี้ใช่หรือไม่:';
      if (this.deleteKind === 'calcVersion') return 'ต้องการลบชุดสูตรคำนวณนี้ใช่หรือไม่:';
      return 'ต้องการลบเวอร์ชันนี้ใช่หรือไม่:';
    },
    deleteTargetLabel() {
      if (!this.deleteTarget) return '';
      if (this.deleteKind === 'year') {
        return `${this.deleteTarget.taxYearCode} (พ.ศ. ${this.deleteTarget.beYear})`;
      }
      if (this.deleteKind === 'questionVersion') {
        const parent = taxConfigV2Store.findVersion(this.deleteQuestionParentId);
        return `${parent ? parent.versionCode + ' / ' : ''}${this.deleteTarget.versionCode}`;
      }
      if (this.deleteKind === 'calcVersion') {
        const parent = taxConfigV2Store.findVersion(this.deleteCalcParentId);
        return `${parent ? parent.versionCode + ' / ' : ''}${this.deleteTarget.versionCode}`;
      }
      const year = taxConfigV2Store.findYear(this.deleteYearId);
      return `${year ? year.taxYearCode + ' / ' : ''}${this.deleteTarget.versionCode}`;
    },
    deleteCascadeMessage() {
      if (this.deleteKind === 'questionVersion') {
        return 'การลบไม่สามารถย้อนกลับได้ (cascade ลบ Step และคำถามทั้งหมดในชุดนี้)';
      }
      if (this.deleteKind === 'calcVersion') {
        // State the real blast radius: the count comes from the row itself, which every calc-version
        // response now carries.
        const n = Number(this.deleteTarget?.functionCount) || 0;
        return n > 0
          ? `การลบไม่สามารถย้อนกลับได้ (cascade ลบฟังก์ชัน C# ทั้ง ${n} ตัวในชุดนี้)`
          : 'การลบไม่สามารถย้อนกลับได้ (ชุดนี้ยังไม่มีฟังก์ชัน)';
      }
      return 'การลบไม่สามารถย้อนกลับได้ (cascade ลบข้อมูลลูกทั้งหมด)';
    },
    // Both config versions and question sets are single-active per parent and nothing
    // promotes a sibling — deleting the active one leaves the parent with 0 active.
    deleteWarnZeroActive() {
      if (this.deleteKind !== 'version' && this.deleteKind !== 'questionVersion' && this.deleteKind !== 'calcVersion') return false;
      return Boolean(this.deleteTarget && this.deleteTarget.isActive);
    },
    deleteZeroActiveMessage() {
      if (this.deleteKind === 'questionVersion') {
        return 'ชุดคำถามนี้กำลัง active — หลังลบ เวอร์ชันคอนฟิกนี้จะเหลือ 0 ชุดที่ active (ไม่มีการเลื่อนชุดอื่นอัตโนมัติ)';
      }
      if (this.deleteKind === 'calcVersion') {
        return 'ชุดสูตรนี้กำลัง active — หลังลบ เวอร์ชันคอนฟิกนี้จะเหลือ 0 ชุดที่ active (ไม่มีการเลื่อนชุดอื่นอัตโนมัติ)';
      }
      return 'เวอร์ชันนี้กำลัง active — หลังลบ ปีนี้จะเหลือ 0 active (ไม่มีการเลื่อนเวอร์ชันอื่นอัตโนมัติ)';
    },
  },
  mounted() {
    this.loadYears();
  },
  methods: {
    // ── shared helpers ───────────────────────────────────────────────
    /**
     * **วันตามปฏิทิน** (`effectiveStartDate` / `effectiveEndDate`) — ตัดเอาส่วนวันที่มาแสดงตรง ๆ
     *
     * 🔴 ห้ามแปลง timezone กับค่าพวกนี้เด็ดขาด "ช่วงที่มีผลบังคับใช้" เป็นวันบนปฏิทิน ไม่ใช่จุดเวลา
     * ถ้าเอาไปตีเป็น instant แล้วฉายไปโซนไทย ค่าที่บังเอิญมีเวลา 17:00 ขึ้นไปติดมาด้วย (เช่นที่ยิงเข้ามาทาง
     * API/สคริปต์ import ไม่ได้มาจากหน้าจอ) จะเลื่อนไปอีกหนึ่งวันทันที
     * · ค่าที่ไม่ใช่ ISO เลยจึงตกไปที่ `-` เหมือนเดิม
     */
    formatDate(value) {
      if (!value) return '-';
      const iso = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
      return iso ? iso[0] : '-';
    },
    /**
     * **จุดเวลา** (`updatedDate`) — อ่านเป็น UTC แล้วเอา "วัน" ของเวลาไทย
     *
     * 🔴 ต่างจาก {@link formatDate} คนละเรื่อง: ของเดิมใช้ฟังก์ชันเดียวกันกับวันตามปฏิทิน ซึ่งแปลว่ามันตัด
     * สตริงเป็นวันตาม **UTC** ⇒ อะไรที่บันทึกหลัง 17:00 น. บ้านเราโชว์เป็นเมื่อวาน (ดู `utils/thaiTime.js`)
     */
    formatUpdated(value) {
      return fmtThaiDate(value, '-');
    },
    async loadYears() {
      this.yearsLoading = true;
      try {
        await taxConfigV2Store.loadYears(true);
        // Open the branch that holds the LIVE config by default so the tree is not a wall of
        // collapsed rows: newest ACTIVE year (newest year at all as the fallback), and then the
        // active config version inside it — that is the one whose Tax Question / Tax Calculate
        // sets people come here to read, and burying them two clicks deep helped nobody.
        // Years are multi-active, hence "newest active" rather than "the" active one.
        const first = this.sortedYears.find((y) => y.isActive) || this.sortedYears[0];
        if (first && !this.isYearExpanded(first.yearID)) {
          this.expandedYears[first.yearID] = true;
          // Deliberately NOT awaited: the year rows paint as soon as the years land, and the
          // sub-tree opens underneath them when its own fetch returns.
          this.loadVersionsForYear(first.yearID).then((ok) => {
            if (ok) this.revealActiveVersion(first.yearID);
          });
        }
      } catch (err) {
        this.showError(err?.message || 'โหลดปีภาษีไม่สำเร็จ');
      } finally {
        this.yearsLoading = false;
      }
    },
    // ── year row / expansion ─────────────────────────────────────────
    isYearExpanded(yearId) {
      return Boolean(this.expandedYears[yearId]);
    },
    toggleYearExpand(year) {
      const id = year.yearID;
      if (this.expandedYears[id]) {
        delete this.expandedYears[id];
        return;
      }
      this.expandedYears[id] = true;
      this.loadVersionsForYear(id);
    },
    versionsOf(yearId) {
      return taxConfigV2Store.versionsForYear(yearId);
    },
    isVersionsLoading(yearId) {
      return taxConfigV2Store.isVersionsLoading(yearId);
    },
    versionsError(yearId) {
      return taxConfigV2Store.versionsError(yearId);
    },
    /**
     * Lazily fetch a year's config versions (once per year unless forced after a
     * mutation). A forced reload that fails keeps the last known list on screen
     * (the store never wipes a good bucket) and drops the question-sub-tree cache
     * only of versions that actually disappeared.
     * @returns {Promise<boolean>} true when the list is in sync with the server
     */
    async loadVersionsForYear(yearId, force = false) {
      if (yearId == null) return false;
      if (!force && (taxConfigV2Store.hasVersionsForYear(yearId) || this.isVersionsLoading(yearId))) return true;
      const before = this.versionsOf(yearId).map((v) => v.versionID);
      try {
        await taxConfigV2Store.loadVersionsForYear(yearId, true);
        const alive = new Set(this.versionsOf(yearId).map((v) => v.versionID));
        before.forEach((id) => { if (!alive.has(id)) this.dropQuestionCache(id); });
        return true;
      } catch {
        // The inline error row (+ retry) renders from the store; no snackbar noise.
        return false;
      }
    },
    /**
     * Auto-open ONE config version of a year — the active one (first one as a fallback) — so its
     * Tax Question and Tax Calculate sub-groups are on screen right after the page loads.
     *
     * Only ever called on the initial load: it seeds the tree, it does not police it, so a version
     * the user collapses by hand stays collapsed.
     */
    revealActiveVersion(yearId) {
      const list = this.versionsOf(yearId);
      const target = list.find((v) => v.isActive) || list[0];
      if (!target || this.isVersionExpanded(target.versionID)) return;
      this.expandedVersions[target.versionID] = true;
      this.loadQuestionVersions(target.versionID);
      this.loadCalcVersions(target.versionID);
    },
    // ── Years ────────────────────────────────────────────────────────
    openAddYear() {
      this.yearEditTarget = null;
      this.yearDialog = true;
    },
    openEditYear(year) {
      this.yearEditTarget = { ...year };
      this.yearDialog = true;
    },
    async submitYear(form) {
      const isEdit = form.yearID != null;
      const body = {
        taxYearCode: form.taxYearCode,
        beYear: form.beYear,
        year: form.year,
        description: form.description,
        effectiveStartDate: form.effectiveStartDate,
        effectiveEndDate: form.effectiveEndDate,
        isActive: form.isActive,
      };
      try {
        if (isEdit) {
          const dto = await taxConfigV2Api.updateYear(form.yearID, body);
          taxConfigV2Store.upsertYear(dto || { ...body, yearID: form.yearID });
          this.showSuccess(`อัปเดตปี ${form.taxYearCode} แล้ว`);
        } else {
          const dto = await taxConfigV2Api.createYear(body);
          if (dto) taxConfigV2Store.upsertYear(dto);
          this.showSuccess(`สร้างปี ${form.taxYearCode} แล้ว`);
        }
        this.yearDialog = false;
        this.yearEditTarget = null;
      } catch (err) {
        this.showError(err?.message || 'บันทึกปีภาษีไม่สำเร็จ');
      }
    },
    async toggleYearActive(year, value) {
      const prev = year.isActive;
      taxConfigV2Store.reflectYearActive(year.yearID, value);
      try {
        await taxConfigV2Api.setYearActive(year.yearID, Boolean(value));
      } catch (err) {
        taxConfigV2Store.reflectYearActive(year.yearID, prev);
        this.showError(err?.message || 'อัปเดตสถานะปีไม่สำเร็จ');
      }
    },
    openDeleteYear(year) {
      this.deleteKind = 'year';
      this.deleteTarget = year;
      this.deleteYearId = year.yearID;
      this.deleteDialog = true;
    },
    // ── Config versions ──────────────────────────────────────────────
    openAddVersion(year) {
      this.versionDialogMode = 'version';
      this.versionEditTarget = null;
      this.cloneSource = null;
      this.versionYearId = year.yearID;
      this.versionDialog = true;
    },
    openEditVersion(year, version) {
      this.versionDialogMode = 'version';
      this.versionEditTarget = { ...version };
      this.cloneSource = null;
      this.versionYearId = year.yearID;
      this.versionDialog = true;
    },
    openClone(year, version) {
      this.versionDialogMode = 'clone';
      this.versionEditTarget = null;
      this.cloneSource = { ...version };
      this.versionYearId = year.yearID;
      this.versionDialog = true;
    },
    async submitVersionDialog(form) {
      if (this.versionDialogMode === 'clone') return this.submitClone(form);
      return this.submitVersion(form);
    },
    async submitVersion(form) {
      const yearId = this.versionYearId;
      if (yearId == null) return;
      const isEdit = form.versionID != null;
      try {
        if (isEdit) {
          const body = {
            versionCode: form.versionCode,
            description: form.description,
            effectiveStartDate: form.effectiveStartDate,
            effectiveEndDate: form.effectiveEndDate,
            isActive: form.isActive,
          };
          const dto = await taxConfigV2Api.updateVersion(form.versionID, body);
          if (form.isActive) taxConfigV2Store.reflectVersionActive(form.versionID, true);
          if (dto) taxConfigV2Store.upsertVersion(dto);
          this.showSuccess(`อัปเดตเวอร์ชัน ${form.versionCode} แล้ว`);
        } else {
          const body = {
            yearID: yearId,
            versionCode: form.versionCode,
            description: form.description,
            effectiveStartDate: form.effectiveStartDate,
            effectiveEndDate: form.effectiveEndDate,
            isActive: form.isActive,
          };
          const dto = await taxConfigV2Api.createVersion(body);
          if (dto) taxConfigV2Store.upsertVersion(dto);
          if (form.isActive && dto) taxConfigV2Store.reflectVersionActive(dto.versionID, true);
          this.showSuccess(`สร้างเวอร์ชัน ${form.versionCode} แล้ว`);
        }
        this.versionDialog = false;
        this.versionEditTarget = null;
        this.expandedYears[yearId] = true;
        // Re-fetch to reflect the authoritative single-active-per-year state.
        await this.loadVersionsForYear(yearId, true);
      } catch (err) {
        this.showError(err?.message || 'บันทึกเวอร์ชันไม่สำเร็จ');
      }
    },
    async submitClone(form) {
      const yearId = this.versionYearId;
      if (!this.cloneSource || yearId == null) return;
      try {
        const dto = await taxConfigV2Api.cloneVersion(this.cloneSource.versionID, {
          newVersionCode: form.newVersionCode,
          description: form.description,
          copyCategories: form.copyCategories,
          copyRules: form.copyRules,
          activate: form.activate,
        });
        if (dto) taxConfigV2Store.upsertVersion(dto);
        this.versionDialog = false;
        this.cloneSource = null;
        this.expandedYears[yearId] = true;
        await this.loadVersionsForYear(yearId, true);
        this.showSuccess(`โคลนเป็นเวอร์ชัน ${form.newVersionCode} แล้ว`);
      } catch (err) {
        this.showError(err?.message || 'โคลนเวอร์ชันไม่สำเร็จ');
      }
    },
    async toggleVersionActive(year, version, value) {
      this.busyVersionId = version.versionID;
      // Optimistic single-active-per-year reflection.
      taxConfigV2Store.reflectVersionActive(version.versionID, value);
      try {
        await taxConfigV2Api.setVersionActive(version.versionID, Boolean(value));
        // Confirm from the server (siblings auto-off).
        await this.loadVersionsForYear(year.yearID, true);
      } catch (err) {
        await this.loadVersionsForYear(year.yearID, true);
        this.showError(err?.message || 'อัปเดตสถานะเวอร์ชันไม่สำเร็จ');
      } finally {
        this.busyVersionId = null;
      }
    },
    openDeleteVersion(year, version) {
      this.deleteKind = 'version';
      this.deleteTarget = version;
      this.deleteYearId = year.yearID;
      this.deleteDialog = true;
    },
    openEditor(year, version) {
      if (!year || !version) return;
      this.$router.push(`/TaxConfigurationV2/${year.taxYearCode}/${version.versionCode}`);
    },
    // ── Tax Question sub-tree ────────────────────────────────────────
    // A question item binds to a FieldKey that lives in the TaxConfigRuleV2 set of
    // ONE config version, so question sets are authored from inside that version
    // (same shape as V1). Loaded lazily on expand, then cached per versionID.
    isVersionExpanded(versionId) {
      return Boolean(this.expandedVersions[versionId]);
    },
    toggleVersionExpand(version) {
      const id = version.versionID;
      if (this.expandedVersions[id]) {
        delete this.expandedVersions[id];
        return;
      }
      this.expandedVersions[id] = true;
      this.loadQuestionVersions(id);
      this.loadCalcVersions(id);
    },
    questionVersionsFor(versionId) {
      return this.questionVersionsByVersion[versionId] || [];
    },
    isQuestionLoading(versionId) {
      return Boolean(this.qLoading[versionId]);
    },
    questionError(versionId) {
      return this.qError[versionId] || '';
    },
    // Returns true when the sub-list is in sync with the server, false when the fetch failed
    // (callers use it to suppress a success snackbar over a stale/errored sub-tree).
    async loadQuestionVersions(versionId, force = false) {
      if (versionId == null) return false;
      // Cache: fetch once per config version unless explicitly forced (after a
      // toggle/delete). Never stack a second in-flight request.
      if (!force && (this.questionVersionsByVersion[versionId] !== undefined || this.qLoading[versionId])) return true;
      // A forced reload bypasses the cache guard, so two of them can overlap — stamp
      // each request and let only the LAST one issued write the cache / clear the
      // spinner, otherwise a slow earlier response resurrects stale active flags.
      const seq = (this.qSeq[versionId] || 0) + 1;
      this.qSeq[versionId] = seq;
      this.qLoading[versionId] = true;
      this.qError[versionId] = '';
      try {
        const list = await taxQuestionV2Api.getVersions(versionId, true);
        if (seq !== this.qSeq[versionId]) return true;
        this.questionVersionsByVersion[versionId] = Array.isArray(list) ? list : [];
        return true;
      } catch (err) {
        if (seq !== this.qSeq[versionId]) return false;
        // First load: leave the cache empty so collapsing + re-expanding retries the fetch.
        // Forced re-load: keep the last known list under the inline error (never wipe good data).
        if (!force) delete this.questionVersionsByVersion[versionId];
        this.qError[versionId] = err?.message || 'โหลดชุดคำถามไม่สำเร็จ';
        return false;
      } finally {
        if (seq === this.qSeq[versionId]) this.qLoading[versionId] = false;
      }
    },
    /** Forget everything cached for ONE config version (it was deleted / vanished). Covers BOTH
     *  sub-trees: a version can only vanish as a whole, so leaving the calc cache behind would
     *  resurrect a dead version's formula list the next time an id got reused. */
    dropQuestionCache(versionId) {
      delete this.expandedVersions[versionId];
      delete this.questionVersionsByVersion[versionId];
      delete this.qLoading[versionId];
      delete this.qError[versionId];
      delete this.qSeq[versionId];
      delete this.calcVersionsByVersion[versionId];
      delete this.cLoading[versionId];
      delete this.cError[versionId];
      delete this.cSeq[versionId];
    },
    suggestQuestionVersionCode(configVersionId) {
      const list = this.questionVersionsFor(configVersionId);
      const used = new Set(list.map((q) => String(q.versionCode || '').trim().toUpperCase()));
      let n = list.length + 1;
      while (used.has(`Q${n}`)) n += 1;
      return `Q${n}`;
    },
    openQuestionEditor(configVersionId, questionVersionId) {
      if (configVersionId == null || questionVersionId == null) return;
      this.$router.push(`/TaxQuestionV2/${configVersionId}/${questionVersionId}`);
    },
    addQuestionVersion(configVersionId) {
      if (configVersionId == null) return;
      // The builder treats a non-numeric id as "no version selected" and opens its add-mode block.
      this.$router.push(`/TaxQuestionV2/${configVersionId}/new`);
    },
    openQuestionMeta(configVersionId, qv) {
      this.qVersionDialogMode = 'edit';
      this.qVersionTarget = { ...qv };
      this.qVersionParentId = configVersionId;
      this.qVersionSuggestedCode = '';
      this.qVersionDialog = true;
    },
    openQuestionClone(configVersionId, qv) {
      this.qVersionDialogMode = 'clone';
      this.qVersionTarget = { ...qv };
      this.qVersionParentId = configVersionId;
      this.qVersionSuggestedCode = this.suggestQuestionVersionCode(configVersionId);
      this.qVersionDialog = true;
    },
    async submitQuestionVersionDialog(form) {
      const parentId = this.qVersionParentId;
      const target = this.qVersionTarget;
      if (!target || parentId == null) return;
      const isClone = this.qVersionDialogMode === 'clone';
      this.qVersionSaving = true;
      try {
        if (isClone) {
          await taxQuestionV2Api.cloneVersion(target.questionVersionID, {
            versionCode: form.versionCode,
            description: form.description || null,
          });
        } else {
          // versionCode is immutable server-side; isActive is passed through so a
          // metadata save never silently deactivates the set.
          await taxQuestionV2Api.updateVersion(target.questionVersionID, {
            description: form.description || null,
            resumePromptTh: form.resumePromptTh || null,
            isActive: Boolean(form.isActive),
          });
        }
        this.qVersionDialog = false;
        const refreshed = await this.loadQuestionVersions(parentId, true);
        // Don't claim success over a sub-tree that failed to re-read.
        if (refreshed) {
          this.showSuccess(isClone
            ? `โคลนเป็นชุดคำถาม ${form.versionCode} แล้ว`
            : `บันทึกข้อมูลชุดคำถาม ${target.versionCode} แล้ว`);
        }
      } catch (err) {
        this.showError(err?.message || (isClone ? 'โคลนชุดคำถามไม่สำเร็จ' : 'บันทึกชุดคำถามไม่สำเร็จ'));
      } finally {
        this.qVersionSaving = false;
      }
    },
    async toggleQuestionActive(configVersionId, qv, value) {
      this.busyQuestionId = qv.questionVersionID;
      try {
        await taxQuestionV2Api.setVersionActive(qv.questionVersionID, Boolean(value));
        // Single-active-per-config-version is enforced server-side (siblings auto-off),
        // so re-read instead of flipping locally.
        const refreshed = await this.loadQuestionVersions(configVersionId, true);
        // The re-fetch can fail on its own; don't claim success over an errored sub-tree.
        if (refreshed) this.showSuccess(`อัปเดตสถานะชุดคำถาม ${qv.versionCode} แล้ว`);
      } catch (err) {
        this.showError(err?.message || 'อัปเดตสถานะชุดคำถามไม่สำเร็จ');
        await this.loadQuestionVersions(configVersionId, true);
      } finally {
        this.busyQuestionId = null;
      }
    },
    openDeleteQuestionVersion(configVersionId, qv) {
      this.deleteKind = 'questionVersion';
      this.deleteTarget = qv;
      this.deleteQuestionParentId = configVersionId;
      this.deleteDialog = true;
    },

    // ── Tax Question import / export (move a set between machines) ────
    /**
     * Download ONE question set as the portable `.json` bundle — the replacement for the
     * hand-run python migrate scripts (Local → Dev → UAT).
     *
     * The endpoint answers with a normal `ResponseModel`, not a file, so the download is made
     * here from a Blob (same as V1's `TaxConfiguration.vue` → `onExportJson`).
     * 🔴 The object URL is revoked on the NEXT TASK, not immediately: Safari cancels an
     * in-flight download when its blob URL disappears in the same task.
     */
    async onExportQuestionVersion(qv) {
      if (!qv || qv.questionVersionID == null) return;
      // Shares the row's busy flag with the active toggle — one row, one in-flight write/read.
      if (this.busyQuestionId === qv.questionVersionID) return;
      this.busyQuestionId = qv.questionVersionID;
      try {
        const bundle = await taxQuestionV2Api.exportVersion(qv.questionVersionID);
        const cfgCode = (bundle && bundle.source && bundle.source.configVersionCode) || 'config';
        const setCode = (bundle && bundle.source && bundle.source.questionVersionCode)
          || qv.versionCode
          || `v${qv.questionVersionID}`;
        const fileName = `taxquestion-${this.safeFilePart(cfgCode)}-${this.safeFilePart(setCode)}.json`;
        const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 0);
        this.showSuccess(`ส่งออกชุดคำถาม ${setCode} แล้ว — ${fileName}`);
      } catch (err) {
        this.showError(err?.message || 'ส่งออกชุดคำถามไม่สำเร็จ');
      } finally {
        this.busyQuestionId = null;
      }
    },
    /** Keep a code that came off the wire from turning into a path or an unsaveable name. */
    safeFilePart(value) {
      const cleaned = String(value ?? '').trim().replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
      return cleaned || 'export';
    },
    openQuestionImport(configVersionId) {
      if (configVersionId == null) return;
      // The target is the row the button sits under — `bundle.source.*` is display-only.
      this.qImportParentId = configVersionId;
      this.qImportDialog = true;
    },
    /**
     * The dialog does NOT close itself after a successful import — it hands the result over and
     * lets this page close it, reload the sub-tree and decide what the user is told.
     * @param {Object} res `ImportTaxQuestionBundleV2Result`
     */
    async onQuestionImported(res) {
      const parentId = this.qImportParentId;
      this.qImportDialog = false;
      const refreshed = await this.loadQuestionVersions(parentId, true);
      // Don't claim success over a sub-tree that failed to re-read (same rule as the clone path).
      if (!refreshed) return;
      const code = (res && res.versionCode) || '';
      const steps = (res && res.stepCount) || 0;
      const items = (res && res.itemCount) || 0;
      let msg = `นำเข้าสำเร็จ: ${code} · ${steps} Section · ${items} คำถาม`;
      if (res && res.versionCodeAdjusted) msg += ' (รหัสชนของเดิม จึงเปลี่ยนให้)';
      const warnings = (res && Array.isArray(res.warnings)) ? res.warnings : [];
      if (warnings.length) {
        // 🔴 Warnings are shown as an ERROR on purpose: rows that could not be remapped mean the
        // set that just landed is NOT the set that was exported, and a silent partial load is
        // worse than a refusal. (Same call as TaxPlaygroundV2's dropped-answer import.)
        msg += ` · มีคำเตือน ${warnings.length} รายการ`;
        const dropped = ((res && res.droppedCalcFunctionCount) || 0) + ((res && res.droppedCalcConfigCount) || 0);
        if (dropped) msg += ` (ตัดการคำนวณออก ${dropped} จุด)`;
        this.showError(msg);
        warnings.forEach((w) => console.warn('[taxquestion-import]', w));
        return;
      }
      this.showSuccess(msg);
    },

    // ── Tax Calculate sub-tree ───────────────────────────────────────
    // Deliberately a mirror of the Tax Question block above: same cache-once-per-config-version rule,
    // same out-of-order request guard, same "don't claim success over an errored sub-tree" discipline.
    calcVersionsFor(versionId) {
      return this.calcVersionsByVersion[versionId] || [];
    },
    isCalcLoading(versionId) {
      return Boolean(this.cLoading[versionId]);
    },
    calcError(versionId) {
      return this.cError[versionId] || '';
    },
    async loadCalcVersions(versionId, force = false) {
      if (versionId == null) return false;
      if (!force && (this.calcVersionsByVersion[versionId] !== undefined || this.cLoading[versionId])) return true;
      const seq = (this.cSeq[versionId] || 0) + 1;
      this.cSeq[versionId] = seq;
      this.cLoading[versionId] = true;
      this.cError[versionId] = '';
      try {
        const list = await taxCalculateV2Api.listVersions(versionId, true);
        if (seq !== this.cSeq[versionId]) return true;
        this.calcVersionsByVersion[versionId] = Array.isArray(list) ? list : [];
        return true;
      } catch (err) {
        if (seq !== this.cSeq[versionId]) return false;
        if (!force) delete this.calcVersionsByVersion[versionId];
        this.cError[versionId] = err?.message || 'โหลดชุดสูตรคำนวณไม่สำเร็จ';
        return false;
      } finally {
        if (seq === this.cSeq[versionId]) this.cLoading[versionId] = false;
      }
    },
    suggestCalcVersionCode(configVersionId) {
      const list = this.calcVersionsFor(configVersionId);
      const used = new Set(list.map((c) => String(c.versionCode || '').trim().toUpperCase()));
      let n = list.length + 1;
      while (used.has(`C${n}`)) n += 1;
      return `C${n}`;
    },
    openCalcEditor(configVersionId, calcVersionId) {
      if (configVersionId == null || calcVersionId == null) return;
      this.$router.push(`/TaxCalculateV2/${configVersionId}/${calcVersionId}`);
    },
    // Unlike the question builder there is no "/new" page to route to: a calc version is created
    // here, then Edit drills into its formula list.
    addCalcVersion(configVersionId) {
      if (configVersionId == null) return;
      this.cVersionDialogMode = 'create';
      this.cVersionTarget = null;
      this.cVersionParentId = configVersionId;
      this.cVersionSuggestedCode = this.suggestCalcVersionCode(configVersionId);
      this.cVersionDialog = true;
    },
    openCalcMeta(configVersionId, cv) {
      this.cVersionDialogMode = 'edit';
      this.cVersionTarget = { ...cv };
      this.cVersionParentId = configVersionId;
      this.cVersionSuggestedCode = '';
      this.cVersionDialog = true;
    },
    openCalcClone(configVersionId, cv) {
      this.cVersionDialogMode = 'clone';
      this.cVersionTarget = { ...cv };
      this.cVersionParentId = configVersionId;
      this.cVersionSuggestedCode = this.suggestCalcVersionCode(configVersionId);
      this.cVersionDialog = true;
    },
    async submitCalcVersionDialog(form) {
      const parentId = this.cVersionParentId;
      if (parentId == null) return;
      const mode = form?.mode || this.cVersionDialogMode;
      this.cVersionSaving = true;
      try {
        if (mode === 'clone') {
          await taxCalculateV2Api.cloneVersion(form.calcVersionID, {
            versionCode: form.versionCode,
            description: form.description || null,
          });
        } else if (mode === 'create') {
          await taxCalculateV2Api.createVersion(parentId, {
            versionCode: form.versionCode,
            description: form.description || null,
            isActive: false,
          });
        } else {
          // versionCode IS editable here (the backend re-checks uniqueness); isActive is echoed so a
          // metadata save never doubles as an activation change.
          await taxCalculateV2Api.updateVersion(form.calcVersionID, {
            versionCode: form.versionCode,
            description: form.description || null,
            isActive: Boolean(form.isActive),
          });
        }
        this.cVersionDialog = false;
        const refreshed = await this.loadCalcVersions(parentId, true);
        if (refreshed) {
          const said = {
            clone: `โคลนเป็นชุดสูตร ${form.versionCode} แล้ว`,
            create: `สร้างชุดสูตร ${form.versionCode} แล้ว`,
            edit: `บันทึกข้อมูลชุดสูตร ${form.versionCode} แล้ว`,
          };
          this.showSuccess(said[mode] || said.edit);
        }
      } catch (err) {
        const failed = {
          clone: 'โคลนชุดสูตรไม่สำเร็จ',
          create: 'สร้างชุดสูตรไม่สำเร็จ',
          edit: 'บันทึกชุดสูตรไม่สำเร็จ',
        };
        this.showError(err?.message || failed[mode] || failed.edit);
      } finally {
        this.cVersionSaving = false;
      }
    },
    async toggleCalcActive(configVersionId, cv, value) {
      this.busyCalcId = cv.calcVersionID;
      try {
        await taxCalculateV2Api.setVersionActive(cv.calcVersionID, Boolean(value));
        // Single-active-per-config-version is enforced server-side (siblings auto-off), so re-read
        // instead of flipping locally.
        const refreshed = await this.loadCalcVersions(configVersionId, true);
        if (refreshed) this.showSuccess(`อัปเดตสถานะชุดสูตร ${cv.versionCode} แล้ว`);
      } catch (err) {
        this.showError(err?.message || 'อัปเดตสถานะชุดสูตรไม่สำเร็จ');
        await this.loadCalcVersions(configVersionId, true);
      } finally {
        this.busyCalcId = null;
      }
    },
    openDeleteCalcVersion(configVersionId, cv) {
      this.deleteKind = 'calcVersion';
      this.deleteTarget = cv;
      this.deleteCalcParentId = configVersionId;
      this.deleteDialog = true;
    },

    // ── Tax Calculate import / export (move a formula set between machines) ────
    // Deliberately the same three methods as the question pair above — same Blob download, same
    // "the parent closes the dialog", same "warnings go out as an error".
    /**
     * Download ONE formula set as the portable `.json` bundle.
     *
     * The endpoint answers with a normal `ResponseModel`, not a file, so the download is made
     * here from a Blob.
     * 🔴 The object URL is revoked on the NEXT TASK, not immediately: Safari cancels an
     * in-flight download when its blob URL disappears in the same task.
     */
    async onExportCalcVersion(cv) {
      if (!cv || cv.calcVersionID == null) return;
      // Shares the row's busy flag with the active toggle — one row, one in-flight write/read.
      if (this.busyCalcId === cv.calcVersionID) return;
      this.busyCalcId = cv.calcVersionID;
      try {
        const bundle = await taxCalculateV2Api.exportVersion(cv.calcVersionID);
        const cfgCode = (bundle && bundle.source && bundle.source.configVersionCode) || 'config';
        const setCode = (bundle && bundle.source && bundle.source.calcVersionCode)
          || cv.versionCode
          || `v${cv.calcVersionID}`;
        const fileName = `taxcalc-${this.safeFilePart(cfgCode)}-${this.safeFilePart(setCode)}.json`;
        const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 0);
        this.showSuccess(`ส่งออกชุดสูตร ${setCode} แล้ว — ${fileName}`);
      } catch (err) {
        this.showError(err?.message || 'ส่งออกชุดสูตรไม่สำเร็จ');
      } finally {
        this.busyCalcId = null;
      }
    },
    openCalcImport(configVersionId) {
      if (configVersionId == null) return;
      // The target is the row the button sits under — `bundle.source.*` is display-only.
      this.cImportParentId = configVersionId;
      this.cImportDialog = true;
    },
    /**
     * The dialog does NOT close itself after a successful import — it hands the result over and
     * lets this page close it, reload the sub-tree and decide what the user is told.
     * @param {Object} res `ImportCalcBundleResult`
     */
    async onCalcImported(res) {
      const parentId = this.cImportParentId;
      this.cImportDialog = false;
      const refreshed = await this.loadCalcVersions(parentId, true);
      // Don't claim success over a sub-tree that failed to re-read (same rule as the clone path).
      if (!refreshed) return;
      const code = (res && res.versionCode) || '';
      const functions = (res && res.functionCount) || 0;
      let msg = `นำเข้าสำเร็จ: ${code} · ${functions} สูตร`;
      if (res && res.versionCodeAdjusted) msg += ' (รหัสชนของเดิม จึงเปลี่ยนให้)';
      const warnings = (res && Array.isArray(res.warnings)) ? res.warnings : [];
      const compileErrors = (res && res.compileErrorCount) || 0;
      if (warnings.length || compileErrors) {
        // 🔴 Warnings are shown as an ERROR on purpose: a formula that did not compile here, or a
        // `fieldKey` this machine does not have, means the set that just landed is NOT the set that
        // was exported — and a silent partial load is worse than a refusal.
        if (compileErrors) msg += ` · คอมไพล์ไม่ผ่าน ${compileErrors} สูตร`;
        if (warnings.length) msg += ` · มีคำเตือน ${warnings.length} รายการ`;
        this.showError(msg);
        warnings.forEach((w) => console.warn('[taxcalc-import]', w));
        return;
      }
      this.showSuccess(msg);
    },

    // ── Config-version bundle (export / import) ──────────────────────
    // One .json carries the config version header + ALL categories + ALL rules + the calc /
    // question sets the user ticked. It is composed server-side from the two child bundles, so
    // this page only picks the selection, downloads the file and — on import — reloads the year.
    /**
     * Open the export dialog for ONE config version. The calc / question caches are lazy (filled
     * on expand only), so a row that was never expanded has nothing to tick yet — warm BOTH
     * caches first (the loaders are no-ops when the cache is already there).
     */
    async openConfigExport(year, version) {
      if (!year || !version || version.versionID == null) return;
      // Shares the row's busy flag with the active toggle — one row, one in-flight read/write.
      if (this.busyVersionId === version.versionID) return;
      const id = version.versionID;
      this.busyVersionId = id;
      try {
        const [qOk, cOk] = await Promise.all([this.loadQuestionVersions(id), this.loadCalcVersions(id)]);
        // No dialog over lists we could not read: an export that silently carried nothing
        // because the fetch failed would look like a deliberate "no children" choice.
        if (!qOk || !cOk) {
          this.showError('โหลดชุดคำถาม / ชุดสูตรของเวอร์ชันนี้ไม่สำเร็จ — ลองใหม่อีกครั้ง');
          return;
        }
      } finally {
        this.busyVersionId = null;
      }
      this.cfgExportYear = year;
      this.cfgExportVersion = version;
      this.cfgExportDialog = true;
    },
    /**
     * Fired by the export dialog with the ticked ids — the API call and the download happen here
     * (same Blob recipe as `onExportQuestionVersion`, including the deferred `revokeObjectURL`).
     * The dialog stays open while `busy`, and is closed from here once the file is on its way.
     * @param {{calcVersionIds:number[], questionVersionIds:number[]}} sel
     */
    async onConfigExport(sel) {
      const year = this.cfgExportYear;
      const version = this.cfgExportVersion;
      if (!year || !version || version.versionID == null) return;
      if (this.busyVersionId === version.versionID) return;
      this.busyVersionId = version.versionID;
      try {
        const bundle = await taxConfigV2Api.exportBundle(version.versionID, {
          calcVersionIds: (sel && Array.isArray(sel.calcVersionIds)) ? sel.calcVersionIds : [],
          questionVersionIds: (sel && Array.isArray(sel.questionVersionIds)) ? sel.questionVersionIds : [],
        });
        const fileName = `taxconfig-${this.safeFilePart(year.taxYearCode)}-${this.safeFilePart(version.versionCode)}.json`;
        const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        // 🔴 Revoked on the NEXT task, not now: Safari cancels an in-flight download whose blob
        // URL disappears in the same task.
        setTimeout(() => URL.revokeObjectURL(url), 0);
        this.cfgExportDialog = false;
        const calcSets = Array.isArray(bundle && bundle.calcVersions) ? bundle.calcVersions.length : 0;
        const questionSets = Array.isArray(bundle && bundle.questionVersions) ? bundle.questionVersions.length : 0;
        this.showSuccess(
          `ส่งออกเวอร์ชัน ${version.versionCode} แล้ว — ${fileName} (สูตร ${calcSets} ชุด · คำถาม ${questionSets} ชุด)`,
        );
      } catch (err) {
        this.showError(err?.message || 'ส่งออกเวอร์ชันคอนฟิกไม่สำเร็จ');
      } finally {
        this.busyVersionId = null;
      }
    },
    /** Next free `V{n}` in a year — same rule as `suggestQuestionVersionCode`, on the store's list. */
    suggestConfigVersionCode(yearId) {
      const list = this.versionsOf(yearId);
      const used = new Set(list.map((v) => String(v.versionCode || '').trim().toUpperCase()));
      let n = list.length + 1;
      while (used.has(`V${n}`)) n += 1;
      return `V${n}`;
    },
    /**
     * Open the import dialog for ONE tax year. The target is the row the button sits under —
     * `bundle.source.*` is display-only. The year's config versions are fetched first (they are
     * lazy per year) so the suggested code is computed from the real list, not an empty bucket.
     */
    async openConfigImport(year) {
      if (!year || year.yearID == null) return;
      // Cache-guarded: a no-op when the year was already expanded. A failed fetch only degrades
      // the suggested code (the server auto-increments a collision anyway), so it is not fatal here.
      await this.loadVersionsForYear(year.yearID);
      this.cfgImportYear = year;
      this.cfgImportDialog = true;
    },
    /**
     * The dialog does NOT close itself after a successful import — it hands the result over and
     * lets this page close it, reload the year's versions and decide what the user is told.
     * @param {Object} res `ImportTaxConfigBundleV2Result`
     */
    async onConfigImported(res) {
      const year = this.cfgImportYear;
      const yearId = year ? year.yearID : null;
      this.cfgImportDialog = false;
      if (yearId == null) return;
      // Open the branch so the new (inactive) version is on screen, then re-read it from the server.
      this.expandedYears[yearId] = true;
      const refreshed = await this.loadVersionsForYear(yearId, true);
      // Don't claim success over a tree that failed to re-read (same rule as the clone path).
      if (!refreshed) return;
      const code = (res && res.versionCode) || '';
      const categories = (res && res.categoryCount) || 0;
      const rules = (res && res.ruleCount) || 0;
      const calcSets = (res && Array.isArray(res.calcVersions)) ? res.calcVersions.length : 0;
      const questionSets = (res && Array.isArray(res.questionVersions)) ? res.questionVersions.length : 0;
      let msg = `นำเข้าสำเร็จ: ${code} · ${categories} หมวด · ${rules} กติกา · สูตร ${calcSets} ชุด · คำถาม ${questionSets} ชุด`;
      if (res && res.versionCodeAdjusted) msg += ' (รหัสชนของเดิม จึงเปลี่ยนให้)';
      const warnings = (res && Array.isArray(res.warnings)) ? res.warnings : [];
      const droppedCalcConfig = (res && res.totalDroppedCalcConfigCount) || 0;
      const compileErrors = (res && res.totalCompileErrorCount) || 0;
      if (warnings.length || droppedCalcConfig > 0 || compileErrors > 0) {
        // 🔴 Shown as an ERROR on purpose: a CALCULATE item whose calc set was not found never
        // computes, a formula that did not compile here is dead, and a FieldKey the file did not
        // carry is unbound — the version that just landed is NOT the one that was exported, and a
        // silent partial load is worse than a refusal.
        if (droppedCalcConfig) msg += ` · ข้อ CALCULATE ที่จะไม่คำนวณ ${droppedCalcConfig} ข้อ`;
        if (compileErrors) msg += ` · คอมไพล์ไม่ผ่าน ${compileErrors} สูตร`;
        if (warnings.length) msg += ` · มีคำเตือน ${warnings.length} รายการ`;
        this.showError(msg);
        warnings.forEach((w) => console.warn('[taxconfig-import]', w));
        return;
      }
      this.showSuccess(msg);
    },

    // ── Delete ───────────────────────────────────────────────────────
    async confirmDelete() {
      if (!this.deleteTarget) return;
      this.deleting = true;
      try {
        if (this.deleteKind === 'year') {
          const yearId = this.deleteTarget.yearID;
          const doomedVersionIds = this.versionsOf(yearId).map((v) => v.versionID);
          await taxConfigV2Api.deleteYear(yearId);
          // Forget the question caches of every version that went away with the year.
          doomedVersionIds.forEach((id) => this.dropQuestionCache(id));
          delete this.expandedYears[yearId];
          taxConfigV2Store.removeYear(yearId); // also clears its version bucket
          this.showSuccess('ลบปีภาษีแล้ว');
        } else if (this.deleteKind === 'calcVersion') {
          const parentId = this.deleteCalcParentId;
          await taxCalculateV2Api.deleteVersion(this.deleteTarget.calcVersionID);
          const refreshed = await this.loadCalcVersions(parentId, true);
          // Delete succeeded; only claim it when the sub-tree actually reflects the new state.
          if (refreshed) this.showSuccess(`ลบชุดสูตร ${this.deleteTarget.versionCode} แล้ว`);
        } else if (this.deleteKind === 'questionVersion') {
          const parentId = this.deleteQuestionParentId;
          await taxQuestionV2Api.deleteVersion(this.deleteTarget.questionVersionID);
          const refreshed = await this.loadQuestionVersions(parentId, true);
          // Delete succeeded; only claim it when the sub-tree actually reflects the new state.
          if (refreshed) this.showSuccess(`ลบชุดคำถาม ${this.deleteTarget.versionCode} แล้ว`);
        } else {
          await taxConfigV2Api.deleteVersion(this.deleteTarget.versionID);
          this.dropQuestionCache(this.deleteTarget.versionID);
          taxConfigV2Store.removeVersion(this.deleteTarget.versionID);
          this.showSuccess('ลบเวอร์ชันแล้ว');
        }
        this.deleteDialog = false;
        this.deleteTarget = null;
        this.deleteYearId = null;
        this.deleteQuestionParentId = null;
        this.deleteCalcParentId = null;
      } catch (err) {
        this.showError(err?.message || 'ลบไม่สำเร็จ');
      } finally {
        this.deleting = false;
      }
    },
    // ── Global prompt settings ───────────────────────────────────────
    // Global singleton — nothing on this page owns it, so it just opens; the dialog
    // fetches its own settings + built-in defaults each time it is opened.
    openSettings() {
      this.settingsDialog = true;
    },
    showSuccess(message) {
      this.snackbarMessage = message;
      this.snackbarColor = 'success';
      this.snackbar = true;
    },
    showError(message) {
      this.snackbarMessage = message;
      this.snackbarColor = 'error';
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
/* ── Tree rows (ported from V1 TaxConfiguration.vue so V2 reads identically) ── */
.cfg-tree-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 0;
  font: inherit;
  text-align: left;
  color: inherit;
}

.cfg-tree-chevron {
  transition: transform 0.2s ease;
}

.cfg-tree-chevron.is-open {
  transform: rotate(90deg);
}

.cfg-tree-title-block {
  display: inline-flex;
  flex-direction: column;
}

.cfg-tree-title {
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
}

.cfg-tree-subtitle {
  font-size: 0.75rem;
  color: #6b7280;
}

.cfg-version-child-row td {
  background: rgba(0, 0, 0, 0.02);
}

.cfg-version-child-cell {
  position: relative;
  padding-left: 36px !important;
}

.cfg-version-branch {
  position: absolute;
  left: 16px;
  top: 50%;
  width: 14px;
  height: 1px;
  background: #c4c4c4;
}

.cfg-version-branch::before {
  content: '';
  position: absolute;
  left: 0;
  top: -16px;
  width: 1px;
  height: 32px;
  background: #c4c4c4;
}

/* ── In-page Tax Question + Tax Calculate sub-version trees ───────────────── */
.cfg-subtree-status {
  padding-left: 72px !important;
  font-size: 0.82rem;
  color: #6b7280;
}

.cfg-subtree-status.is-error {
  color: rgb(var(--v-theme-error));
}

.cfg-subgroup-cell {
  position: relative;
  padding-left: 72px !important;
}

.cfg-subgroup-question .cfg-subgroup-cell {
  background: rgba(var(--v-theme-primary), 0.06);
}

.cfg-subgroup-calc .cfg-subgroup-cell {
  background: rgba(103, 58, 183, 0.06);
}

.cfg-subgroup-branch {
  position: absolute;
  left: 52px;
  top: 50%;
  width: 14px;
  height: 1px;
  background: #c4c4c4;
}

.cfg-subgroup-branch::before {
  content: '';
  position: absolute;
  left: 0;
  top: -16px;
  width: 1px;
  height: 32px;
  background: #c4c4c4;
}

.cfg-subgroup-header {
  display: inline-flex;
  align-items: center;
}

.cfg-subgroup-title {
  font-weight: 700;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.cfg-subgroup-question .cfg-subgroup-title {
  color: rgb(var(--v-theme-primary));
}

/* deep-purple: the two sub-groups sit in the same tree and must be told apart at a glance */
.cfg-subgroup-calc .cfg-subgroup-title {
  color: #673ab7;
}

.cfg-subversion-calc .cfg-subversion-code {
  color: #673ab7;
}

.cfg-subgroup-count {
  margin-left: 6px;
  font-size: 0.75rem;
  color: #6b7280;
}

.cfg-subgroup-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 16px;
}

/* Individual question-version rows */
.cfg-subversion-row td {
  background: rgba(0, 0, 0, 0.015);
  font-size: 0.85rem;
}

.cfg-subversion-cell {
  position: relative;
  padding-left: 96px !important;
}

.cfg-subversion-branch {
  position: absolute;
  left: 76px;
  top: 50%;
  width: 14px;
  height: 1px;
  background: #c4c4c4;
}

.cfg-subversion-branch::before {
  content: '';
  position: absolute;
  left: 0;
  top: -16px;
  width: 1px;
  height: 32px;
  background: #c4c4c4;
}

.cfg-subversion-code {
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
}

.cfg-subversion-empty-cell {
  padding-left: 96px !important;
  color: #9e9e9e;
  font-size: 0.8rem;
  font-style: italic;
}

.no-data-container {
  padding: 48px 24px;
  text-align: center;
}

.no-data-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.no-data-title {
  font-size: 1.1rem;
  font-weight: 500;
  color: #616161;
}

.no-data-subtitle {
  color: #9e9e9e;
  font-size: 0.875rem;
}

.action-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 180px;
}

.action-btn {
  transition: transform 0.2s ease;
}

.action-btn:hover {
  transform: scale(1.1);
}

.action-btn:deep(.v-btn__underlay) {
  opacity: 0.22;
}

.action-btn:hover:deep(.v-btn__underlay) {
  opacity: 0.32;
}

:deep(.my-data-table .v-table__wrapper) {
  max-height: 620px !important;
  overflow: auto !important;
}

:deep(.my-data-table table) {
  table-layout: fixed;
  min-width: 1400px;
  width: 100%;
}

:deep(.my-data-table thead th),
:deep(.my-data-table tbody td) {
  vertical-align: middle;
}

:deep(.my-data-table tbody td) {
  height: 56px;
  padding: 0 16px;
}
</style>
